import { StartTimerDto, TimerDto, UpdatePlannedTaskResponse, UpdateTaskTime } from 'shovel-lib/types';
import { actionCreator, asyncActionsCreator } from '../common/utils';
import { SessionPlace } from '../undo/types';

const asyncActions = asyncActionsCreator('@@timer');
const action = actionCreator('@@timer');

/** Where a timer was started, for the activity log's Usage page (the backend keeps only these). */
export type TimerStartedFrom = 'session_popup' | 'task_details' | 'calendar' | 'top_bar';

/**
 * What ▶ changed besides the timer (startWithMoves), for its Undo: where the sessions it moved were and where they went,
 * its own block too, the pieces it split off and the sessions it took off. `message`: the Undo toast, when it moved
 * others.
 */
export type StartChanges = {
  places: SessionPlace[];
  to: SessionPlace[];
  createdIds: number[];
  removedSessions: unknown[];
  message?: string | null;
};

// extendMinutes: on a session already logged, the countdown's "Add" (the server grows its block by it).
export type StartTimerRequest = StartTimerDto & {
  taskId?: number;
  startedFrom?: TimerStartedFrom;
  extendMinutes?: number;
  changes?: StartChanges;
};

export const start = asyncActions<StartTimerRequest, UpdatePlannedTaskResponse, string>('START');

export const toggle = asyncActions<void, TimerDto, string>('TOGGLE');

// Reset on a session with time logged and no timer (Petr, 2026-10-17): back to untouched, with Undo.
export const clearLoggedTime = action<{ plannedTaskId: number; used: number; isUsed: boolean }>('CLEAR_LOGGED_TIME');

// The pill pausing a timer left running (two check-ins unanswered): paused at that many seconds counted.
export const pauseAt = action<number>('PAUSE_AT');

export const cancel = asyncActions<void, { activeTimer: any; response: TimerDto }, string>('CANCEL');

export const getCurrentTimer = asyncActions<void, TimerDto, string>('GET_CURRENT_TIMER');

export const clearSession = action<void>('CLEAR_SESSION');

export const updateTimerForTask = asyncActions<UpdateTaskTime, TimerDto, string>('UPDATE_TIMER_FOR_TASK');

// Starts a session's timer where the smart default put it (utils/timerStart.ts): its block, and any study sessions after
// it, moved first, all at once; then the timer starts on the block's new length.
// With pieces or sessions to take off (bumping the day down, utils/bump.ts) it saves through /planner/rearrange, and
// `undoMessage` is the Undo toast it shows once the timer has started, when it moved other sessions.
export const startWithMoves = action<
  StartTimerRequest & {
    taskId?: number;
    moves: { id: string | number; start: number; end: number }[];
    pieces?: { fromId: string | number; start: number; end: number }[];
    removeIds?: (string | number)[];
    undoMessage?: string | null;
  }
>('START_WITH_MOVES');
